// Sign-in screen rendered inside MSAL's <UnauthenticatedTemplate>.
// One action: kick off the MSAL redirect to Microsoft. The page
// navigates away on success, so the "connecting" state lives on
// without ever needing to reset — except when loginRedirect
// rejects (rare), in which case we revert to idle and surface a
// quiet inline message.
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./msalConfig";
import "./SignInScreen.css";

type Phase = "idle" | "connecting" | "error";

export function SignInScreen() {
  const { instance } = useMsal();
  const [phase, setPhase] = useState<Phase>("idle");

  const signIn = () => {
    if (phase === "connecting") return;
    setPhase("connecting");
    // loginRedirect navigates away on success — no need to reset.
    // On rejection (rare), drop back to idle with the danger note.
    void instance.loginRedirect(loginRequest).catch(() => setPhase("error"));
  };

  const busy = phase === "connecting";

  return (
    <div className="bsp-signin-stage" role="presentation">
      <main
        className={`bsp-signin-card${busy ? " is-busy" : ""}`}
        aria-labelledby="bsp-signin-title"
      >
        <div className="bsp-signin-progress" aria-hidden="true">
          <div className="bsp-signin-progress-runner" />
        </div>
        <div className="bsp-signin-keyline" aria-hidden="true" />
        <div className="bsp-signin-body">
          <img
            className="bsp-signin-logo"
            src="/assets/bsp-logo-eng.png"
            alt="BSP Engineering"
            width={147}
            height={56}
          />
          <div className="bsp-signin-eyebrow">Internal Tools Hub</div>
          <h1 id="bsp-signin-title" className="bsp-signin-title">Sign in</h1>
          <button
            type="button"
            className="bsp-signin-btn"
            onClick={signIn}
            disabled={busy}
            aria-busy={busy}
          >
            {busy ? (
              <>
                <span className="bsp-signin-spinner" aria-hidden="true" />
                <span>Connecting to Microsoft…</span>
              </>
            ) : (
              <>
                <MicrosoftGlyph />
                <span>Sign in with Microsoft</span>
              </>
            )}
          </button>
          {phase === "error" ? (
            <div className="bsp-signin-error" role="alert">
              Couldn’t reach Microsoft sign-in. Try again.
            </div>
          ) : null}
          <div className="bsp-signin-note">
            <LockIcon />
            <span>Authorised BSP personnel only</span>
          </div>
        </div>
        <div className="bsp-signin-footer">BSP · A Drillcut company</div>
      </main>
    </div>
  );
}

// Microsoft four-square glyph. 9×9 squares with a 1px gutter in a
// 21×21 viewBox, brand hexes per the design spec.
function MicrosoftGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect width="9" height="9" fill="#F25022" />
      <rect x="12" width="9" height="9" fill="#7FBA00" />
      <rect y="12" width="9" height="9" fill="#00A4EF" />
      <rect x="12" y="12" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Lucide-style lock: 1.75px stroke, square caps, currentColor.
function LockIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
